import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { Component, OnInit, Renderer2, ElementRef, ViewChild, OnDestroy } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { SetFormPristine } from '@ngxs/form-plugin';
import { routeNames } from '@shared/models';
import { SharedService } from '@shared/services';
import { FileState } from '@maz/store';
import { ListState } from '@app/store/list.state';
import { FormatEpisode, Format } from '@app/models';
import { searchStagger, episodeAnimation, FormatExpand } from '@app/animations';
import { PortalService } from '@app/services';
import { PortalState } from '@app/store/portal.state';
import * as actions from '@app/store/list.actions';

@Component({
  selector: 'prp-list-item-expand',
  templateUrl: './list-item-expand.component.html',
  styleUrls: ['./list-item-expand.component.scss'],
  animations: [FormatExpand, episodeAnimation, searchStagger],
})
export class ListItemExpandComponent implements OnInit, OnDestroy {
  private formatSelected: string;
  private episodeSelected: string;
  private itemPosition: number;
  private scrollPosition: number;
  private destroy$ = new Subject<void>();
  position;
  totalChildResults;
  childsLoaded;
  private RESULT_COUNT = 10;
  private EPISODE_AUTOSELECT = 1;

  @Select(ListState.format) format$: Observable<Format>;
  @Select(ListState.episodeSelected) episode$: Observable<string>;
  @Select(PortalState.rtl2Employee)
  producer$: Observable<boolean>;
  @Select(ListState.searchResults) data$: Observable<Format[]>;
  @ViewChild('el1', { static: true }) el: ElementRef;

  currentState;
  constructor(
    private renderer: Renderer2,
    private store: Store,
    private sharedService: SharedService,
    private portalService: PortalService
  ) {
    this.store
      .select(ListState.formatSelected)
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload: string) => {
        this.formatSelected = payload;
      });

    this.store
      .select(ListState.episodeSelected)
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload: string) => {
        this.episodeSelected = payload;
      });

    this.store
      .select(PortalState.itemPosition)
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload: number) => {
        this.itemPosition = payload;
      });

    this.store
      .select(PortalState.scrollPosition)
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload: number) => {
        this.scrollPosition = payload;
      });
  }

  ngOnInit() {
    this.setStyle('position', 'absolute');
    this.setStyle('display', 'none');
    this.setStyle('transition', 'all 0.5s');
    this.setStyle('width', '100%');
    this.setStyle('height', '100px');
    this.setStyle('zIndex', '7');

    setTimeout(() => {
      this.setStyle('top', this.itemPosition - 12 - this.scrollPosition + 'px');
      this.setStyle('display', 'block');
      this.currentState = 'state2';
    });

    this.format$.pipe(take(1)).subscribe((data) => {
      if (+data?.childs?.length === 1) this.selectEpisode(data.childs[0]);
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setStyle(prop: string, value: string) {
    this.renderer.setStyle(this.el.nativeElement, prop, value);
  }

  hideEpisodes() {
    const callback = this.triggerHideEpisodes.bind(this);
    const pending = this.store.selectSnapshot(ListState.newFilePending);

    pending
      ? this.portalService.checkNewFilePending([], callback)
      : this.portalService.checkChangesPending([], callback);
  }

  triggerHideEpisodes() {
    this.store.dispatch([
      new SetFormPristine('file.mazForm'),
      new actions.SetFormatSelected(null),
      new actions.SetFileSelected(null),
    ]);
    this.sharedService.navigateBaseFirstCurrent(
      {
        [routeNames.contentFormatId]: undefined,
        [routeNames.episodeNumber]: undefined,
      },
      true
    );
  }

  selectEpisode(episode: FormatEpisode) {
    const callback = this.triggerSelectEpisode.bind(this);
    const pending = this.store.selectSnapshot(ListState.newFilePending);

    pending
      ? this.portalService.checkNewFilePending([episode], callback)
      : this.portalService.checkChangesPending([episode], callback);
  }

  triggerSelectEpisode(episode: FormatEpisode) {
    this.store.dispatch([
      new SetFormPristine('file.mazForm'),
      new actions.SetEpisodeSelected(`${episode?.episodeNr}`),
    ]);
    this.sharedService.navigateBaseFirstCurrent(
      { [routeNames.episodeNumber]: episode?.episodeNr },
      true
    );
  }

  isSelected(contentFormatId: number, episodeNr: number): boolean {
    return contentFormatId === +this.formatSelected && episodeNr === +this.episodeSelected;
  }

  loadNewEpisodes() {
    const data = this.store.selectSnapshot(ListState.format);
    if (!data) return;

    this.childsLoaded = data.childs?.length || 0;
    this.totalChildResults = data.totalChildResults;

    if (this.totalChildResults >= this.RESULT_COUNT && this.childsLoaded < this.totalChildResults) {
      this.store.dispatch(new actions.EpisodePagination());
    }
  }
}
